import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { POCService } from "../../../services";
import { GoLocation } from "react-icons/go";
import { toast } from "react-toastify";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

const NewPOCPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("general");
  const [formData, setFormData] = useState({
    Name: "",
    AddressLine1: "",
    AddressLine2: "",
    Postcode: "",
    City: "",
    State: "",
    Country: "Malaysia",
    Map: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      if (!formData[key]) {
        newErrors[key] = `${key} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return; // If validation fails, don't proceed with the API call
    }

    setLoading(true);
    try {
      const response = await POCService.addPOC(formData);
      console.log("POC added successfully:", response);
      toast.success("🎉 POC added successfully!", {
        autoClose: 2000,
      });
      navigate("/poc");
    } catch (error) {
      console.error("Error adding POC:", error);
      toast.error("❌ Failed to add POC", {
        autoClose: 2000,
      });
    } finally {
      setLoading(false); // Reset loading state
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      <div className="flex-1 flex flex-col">
        <div className="p-8 flex flex-col">
          <div className="bg-white shadow p-4 pb-1 rounded-lg">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-0 mb-6">
              {/* Left Section: Icon and Title */}
              <div className="flex items-center gap-4">
                {/* POC Image */}
                <div className="bg-lime-400 p-3 sm:p-4 rounded-full">
                  <GoLocation className="w-10 h-10 sm:w-16 sm:h-16 text-white" />
                </div>
                <h1 className="text-lg font-semibold text-[#111827]">
                  New POC
                </h1>
              </div>

              {/* Right Section: Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full sm:w-auto">
                <Button
                  variant={"hollowGreen"}
                  className="w-full sm:w-auto"
                  onClick={() => navigate("/poc")}
                >
                  Cancel
                </Button>
                <Button disabled={loading} onClick={handleSubmit}>
                  {loading ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          </div>

          {/* Tabs Section */}
          <div className="flex">
            <button
              onClick={() => handleTabSwitch("general")}
              className={`relative mt-5 rounded-tl-xl px-3 pr-10 py-3 text-[14px] font-bold ${
                activeTab === "general"
                  ? "text-[#111827] bg-white"
                  : "text-[#14B8A6] bg-[#F0FDFA]"
              }`}
              style={{
                clipPath: "polygon(0 0, 70% 0, 100% 100%, 0% 100%)",
              }}
            >
              General Info
            </button>
          </div>

          {/* Form Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 p-4 sm:p-6 bg-white shadow rounded-lg">
            {/* POC Name */}
            <div>
              <Label>POC Name</Label>
              <Input
                type="text"
                name="Name"
                placeholder="Enter POC Name"
                value={formData.Name}
                onChange={handleChange}
                className={errors.Name ? "border-red-500" : ""}
              />
              {errors.Name && (
                <p className="text-red-500 text-sm">{errors.Name}</p>
              )}
            </div>

            {/* Address Line 1 & 2 */}
            <div>
              <Label>Address</Label>
              <Input
                type="text"
                name="AddressLine1"
                placeholder="Enter Address"
                value={formData.AddressLine1}
                onChange={handleChange}
                className={errors.AddressLine1 ? "border-red-500" : ""}
              />
              {errors.AddressLine1 && (
                <p className="text-red-500 text-sm">{errors.AddressLine1}</p>
              )}
              <Input
                type="text"
                name="AddressLine2"
                placeholder="Enter Address"
                value={formData.AddressLine2}
                onChange={handleChange}
                className={`mt-2 ${
                  errors.AddressLine2 ? "border-red-500" : ""
                }`}
              />
              {errors.AddressLine2 && (
                <p className="text-red-500 text-sm">{errors.AddressLine2}</p>
              )}
            </div>

            {/* Postcode */}
            <div>
              <Label>Postcode</Label>
              <Input
                type="text"
                name="Postcode"
                placeholder="Enter Postcode"
                value={formData.Postcode}
                onChange={handleChange}
                className={errors.Postcode ? "border-red-500" : ""}
              />
              {errors.Postcode && (
                <p className="text-red-500 text-sm">{errors.Postcode}</p>
              )}
            </div>

            {/* City */}
            <div>
              <Label>City</Label>
              <Input
                type="text"
                name="City"
                placeholder="Enter City"
                value={formData.City}
                onChange={handleChange}
                className={errors.City ? "border-red-500" : ""}
              />
              {errors.City && (
                <p className="text-red-500 text-sm">{errors.City}</p>
              )}
            </div>

            {/* State */}
            <div>
              <Label>State</Label>
              <Input
                type="text"
                name="State"
                placeholder="Enter State"
                value={formData.State}
                onChange={handleChange}
                className={errors.State ? "border-red-500" : ""}
              />
              {errors.State && (
                <p className="text-red-500 text-sm">{errors.State}</p>
              )}
            </div>

            {/* Country */}
            <div>
              <Label>Country</Label>
              <Select
                name="Country"
                value={formData.Country}
                onValueChange={(value) =>
                  handleChange({
                    target: {
                      name: "Country",
                      value: value,
                    },
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Malaysia">Malaysia</SelectItem>
                  <SelectItem value="Indonesia">Indonesia</SelectItem>
                  <SelectItem value="China">China</SelectItem>
                  <SelectItem value="Singapore">Singapore</SelectItem>
                  <SelectItem value="Vietnam">Vietnam</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Map */}
            <div>
              <Label>Google Map URL</Label>
              <Input
                type="text"
                name="Map"
                placeholder="Enter Google Map URL"
                value={formData.Map}
                onChange={handleChange}
                className={errors.Map ? "border-red-500" : ""}
              />
              {errors.Map && (
                <p className="text-red-500 text-sm">{errors.Map}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewPOCPage;
